import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Community } from '@stash/shared-types';
import {
  fetchCommunityAsync,
  fetchCommunitiesAsync,
  updateCommunityAsync,
  uploadCommunityImageAsync,
  addCommunityMemberAsync,
  removeCommunityMemberAsync,
  addCommunityVendorAsync,
  removeCommunityVendorAsync,
  deleteCommunityImageAsync,
} from '../actions/community-actions';

export interface CommunityState {
  currentCommunity: Community | null;
  communities: Community[];
  isLoading: boolean;
  isUpdating: boolean;
  isUploadingImage: boolean;
  error: string | null;
  updateError: string | null;
  imageUploadError: string | null;
  hasMoreCommunities: boolean;
  totalCommunities: number;
}

interface OptimisticUpdatePayload {
  id: string;
  updates: Partial<Community>;
}

export type CommunitiesPayload =
  | Community[]
  | { communities?: Community[]; total?: number; hasMore?: boolean; length?: number };

const initialState: CommunityState = {
  currentCommunity: null,
  communities: [],
  isLoading: false,
  isUpdating: false,
  isUploadingImage: false,
  error: null,
  updateError: null,
  imageUploadError: null,
  hasMoreCommunities: true,
  totalCommunities: 0,
};

function updateCommunityInState(state: CommunityState, updated: Community): void {
  if (state.currentCommunity && state.currentCommunity.recordID === updated.recordID) {
    state.currentCommunity = updated;
  }
  const idx = state.communities.findIndex((c) => c.recordID === updated.recordID);
  if (idx !== -1) {
    state.communities[idx] = updated;
  }
}

const communitySlice = createSlice({
  name: 'community',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.updateError = null;
      state.imageUploadError = null;
    },
    clearCurrentCommunity: (state) => {
      state.currentCommunity = null;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    updateCommunityOptimistic: (state, action: PayloadAction<OptimisticUpdatePayload>) => {
      if (state.currentCommunity && state.currentCommunity.recordID === action.payload.id) {
        state.currentCommunity = { ...state.currentCommunity, ...action.payload.updates };
      }
      const communityIndex = state.communities.findIndex((c) => c.recordID === action.payload.id);
      if (communityIndex !== -1) {
        state.communities[communityIndex] = {
          ...state.communities[communityIndex],
          ...action.payload.updates,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommunityAsync.pending, (state: CommunityState) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCommunityAsync.fulfilled, (state: CommunityState, action: PayloadAction<Community>) => {
        state.isLoading = false;
        state.currentCommunity = action.payload;
        state.error = null;
      })
      .addCase(fetchCommunityAsync.rejected, (state: CommunityState, action: PayloadAction<string | undefined>) => {
        state.isLoading = false;
        state.error = action.payload ?? null;
        state.currentCommunity = null;
      });

    builder
      .addCase(fetchCommunitiesAsync.pending, (state: CommunityState) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCommunitiesAsync.fulfilled, (state: CommunityState, action: PayloadAction<CommunitiesPayload>) => {
        state.isLoading = false;
        const payload = action.payload;
        if (Array.isArray(payload)) {
          state.communities = payload;
          state.totalCommunities = payload.length;
          state.hasMoreCommunities = false;
        } else {
          state.communities = payload.communities ?? [];
          state.totalCommunities = payload.total ?? 0;
          state.hasMoreCommunities = payload.hasMore ?? false;
        }
        state.error = null;
      })
      .addCase(fetchCommunitiesAsync.rejected, (state: CommunityState, action: PayloadAction<string | undefined>) => {
        state.isLoading = false;
        state.error = action.payload ?? null;
      });

    builder
      .addCase(updateCommunityAsync.pending, (state: CommunityState) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(updateCommunityAsync.fulfilled, (state: CommunityState, action: PayloadAction<Community>) => {
        state.isUpdating = false;
        updateCommunityInState(state, action.payload);
        state.updateError = null;
      })
      .addCase(updateCommunityAsync.rejected, (state: CommunityState, action: PayloadAction<string | undefined>) => {
        state.isUpdating = false;
        state.updateError = action.payload ?? null;
      });

    builder
      .addCase(uploadCommunityImageAsync.pending, (state: CommunityState) => {
        state.isUploadingImage = true;
        state.imageUploadError = null;
      })
      .addCase(uploadCommunityImageAsync.fulfilled, (state: CommunityState, action: PayloadAction<Community>) => {
        state.isUploadingImage = false;
        updateCommunityInState(state, action.payload);
        state.imageUploadError = null;
      })
      .addCase(uploadCommunityImageAsync.rejected, (state: CommunityState, action: PayloadAction<string | undefined>) => {
        state.isUploadingImage = false;
        state.imageUploadError = action.payload ?? null;
      });

    builder
      .addCase(addCommunityMemberAsync.fulfilled, (state: CommunityState, action: PayloadAction<Community>) => {
        updateCommunityInState(state, action.payload);
      })
      .addCase(addCommunityMemberAsync.rejected, (state: CommunityState, action: PayloadAction<string | undefined>) => {
        state.updateError = action.payload ?? null;
      });

    builder
      .addCase(removeCommunityMemberAsync.fulfilled, (state: CommunityState, action: PayloadAction<Community>) => {
        updateCommunityInState(state, action.payload);
      })
      .addCase(removeCommunityMemberAsync.rejected, (state: CommunityState, action: PayloadAction<string | undefined>) => {
        state.updateError = action.payload ?? null;
      });

    builder
      .addCase(addCommunityVendorAsync.fulfilled, (state: CommunityState, action: PayloadAction<Community>) => {
        updateCommunityInState(state, action.payload);
      })
      .addCase(addCommunityVendorAsync.rejected, (state: CommunityState, action: PayloadAction<string | undefined>) => {
        state.updateError = action.payload ?? null;
      });

    builder
      .addCase(removeCommunityVendorAsync.fulfilled, (state: CommunityState, action: PayloadAction<Community>) => {
        updateCommunityInState(state, action.payload);
      })
      .addCase(removeCommunityVendorAsync.rejected, (state: CommunityState, action: PayloadAction<string | undefined>) => {
        state.updateError = action.payload ?? null;
      });

    builder
      .addCase(deleteCommunityImageAsync.fulfilled, (state: CommunityState, action: PayloadAction<Community>) => {
        updateCommunityInState(state, action.payload);
      })
      .addCase(deleteCommunityImageAsync.rejected, (state: CommunityState, action: PayloadAction<string | undefined>) => {
        state.imageUploadError = action.payload ?? null;
      });
  },
});

export const { clearError, clearCurrentCommunity, setLoading, updateCommunityOptimistic } =
  communitySlice.actions;

export default communitySlice.reducer;
